import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData, selectTheme } from "../../themeSlice";
// import { processQuestions } from "../../questionSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";

const HEADING_MAX_LEN = 300;
const TAB_NAME_MAX_LEN = 30;
const UPLOAD_INSTRUCTION_MAX_LEN = 200;
const ANIMATED_AVATAR_MAX_LEN = 200;
const REVEAL_PAGE_TEXT_MAX_LEN = 100;
const GALLERY_HEADING_MAX_LEN = 100;
const TERMS_MAX_LEN = 500;
const TERMS_HEADING_MAX_LEN = 30;

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [isIntroModelOpen, setisIntroModelOpen] = useState(false);
  // bring in all four values from your theme slice exactly as before
  const { availableThemes, currentTheme, status, data } = useSelector(
    (state) => state.theme
  );

  // left‐side: rules
  const [rules, setRules] = useState([""]);

  // Basic theme details: Title, Step 1 Text, Step 2 Text
  const [title, setTitle] = useState("");
  const [step1Text, setStep1Text] = useState("");
  const [step2Text, setStep2Text] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [galleryEnabled, setGalleryEnabled] = useState(true);
  const [galleryHeading, setGalleryHeading] = useState("");
  const [termsEnabled, setTermsEnabled] = useState(false);
  const [termsText, setTermsText] = useState("");
  const [termsHeading, setTermsHeading] = useState("");

  const [tabName, setTabName] = useState("");
  const [uploadInstructionText, setUploadInstructionText] = useState("");
  const [animatedAvatarText, setAnimatedAvatarText] = useState("");
  const [revealPageText, setRevealPageText] = useState("");

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#000000",
    button_text_color: "#ffffff",
    leaderboard_color: "#000000",
    table_color1: "#000000",
    table_color2: "#ffffff",
  });

  useEffect(() => {
    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    if (!source) return;
    setColors((prev) => ({
      text_color: source.text_color ?? prev.text_color,
      button_color: source.button_color ?? prev.button_color,
      button_text_color: source.button_text_color ?? prev.button_text_color,
      leaderboard_color: source.leaderboard_color ?? prev.leaderboard_color,
      table_color1: source.table_color1 ?? prev.table_color1,
      table_color2: source.table_color2 ?? prev.table_color2,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // fetch questions & theme on mount
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  // when theme data arrives, populate rules + basic theme details
  useEffect(() => {
    if (!data) return;
    setRules(Array.isArray(data.rules) ? data.rules : [""]);
    setTitle(data.follow_text || "");
    setStep1Text(data.step1_text || "");
    setStep2Text(data.step2_text || "");
    setGalleryEnabled(data.gallery_enabled !== false);
    setGalleryHeading(data.gallery_heading || "");
    setTermsEnabled(data.terms_enabled === true || data.terms_enabled === "true");
    setTermsText(data.terms_text || "");
    setTermsHeading(data.terms_heading || "");
    setTabName(data.tab_name || "");
    setUploadInstructionText(data.upload_instruction_text || "");
    setAnimatedAvatarText(data.animated_avatar_text || "");
    setRevealPageText(data.reveal_page_text || "");
  }, [data]);

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 200) {
      Swal.fire(
        "Limit exceeded",
        "Rules can be at most 200 characters. Extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire(
        "Limit reached",
        "You can only add up to 6 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At least one rule required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleTitleChange = (val) => {
    if (val.length > HEADING_MAX_LEN) {
      Swal.fire("Limit exceeded", `Heading can be at most ${HEADING_MAX_LEN} characters. Extra text has been trimmed.`, "warning");
      val = val.slice(0, HEADING_MAX_LEN);
    }
    setTitle(val);
  };

  const handleTabNameChange = (val) => {
    if (val.length > TAB_NAME_MAX_LEN) {
      Swal.fire("Limit exceeded", `Tab name can be at most ${TAB_NAME_MAX_LEN} characters. Extra text has been trimmed.`, "warning");
      val = val.slice(0, TAB_NAME_MAX_LEN);
    }
    setTabName(val);
  };

  const handleUploadInstructionChange = (val) => {
    if (val.length > UPLOAD_INSTRUCTION_MAX_LEN) {
      Swal.fire("Limit exceeded", `Upload instruction message can be at most ${UPLOAD_INSTRUCTION_MAX_LEN} characters. Extra text has been trimmed.`, "warning");
      val = val.slice(0, UPLOAD_INSTRUCTION_MAX_LEN);
    }
    setUploadInstructionText(val);
  };

  const handleAnimatedAvatarChange = (val) => {
    if (val.length > ANIMATED_AVATAR_MAX_LEN) {
      Swal.fire("Limit exceeded", `Animated avatar text can be at most ${ANIMATED_AVATAR_MAX_LEN} characters. Extra text has been trimmed.`, "warning");
      val = val.slice(0, ANIMATED_AVATAR_MAX_LEN);
    }
    setAnimatedAvatarText(val);
  };

  const handleGalleryHeadingChange = (val) => {
    if (val.length > GALLERY_HEADING_MAX_LEN) {
      Swal.fire("Limit exceeded", `Gallery heading can be at most ${GALLERY_HEADING_MAX_LEN} characters. Extra text has been trimmed.`, "warning");
      val = val.slice(0, GALLERY_HEADING_MAX_LEN);
    }
    setGalleryHeading(val);
  };

  const handleTermsHeadingChange = (val) => {
    if (val.length > TERMS_HEADING_MAX_LEN) {
      Swal.fire("Limit exceeded", `Terms and condition heading can be at most ${TERMS_HEADING_MAX_LEN} characters. Extra text has been trimmed.`, "warning");
      val = val.slice(0, TERMS_HEADING_MAX_LEN);
    }
    setTermsHeading(val);
  };

  const handleTermsTextChange = (val) => {
    if (val.length > TERMS_MAX_LEN) {
      Swal.fire("Limit exceeded", `Terms and condition message can be at most ${TERMS_MAX_LEN} characters. Extra text has been trimmed.`, "warning");
      val = val.slice(0, TERMS_MAX_LEN);
    }
    setTermsText(val);
  };

  const handleRevealPageTextChange = (val) => {
    if (val.length > REVEAL_PAGE_TEXT_MAX_LEN) {
      Swal.fire("Limit exceeded", `Reveal page text can be at most ${REVEAL_PAGE_TEXT_MAX_LEN} characters. Extra text has been trimmed.`, "warning");
      val = val.slice(0, REVEAL_PAGE_TEXT_MAX_LEN);
    }
    setRevealPageText(val);
  };

  // --- submit all ---
  const handleSubmit = async () => {
    const trimmedRules = rules.map((r) => (r ?? "").trim());

    // Ensure there is at least one rule and no rule row is left blank.
    if (trimmedRules.length === 0 || trimmedRules.every((r) => !r)) {
      await Swal.fire(
        "At least one rule required",
        "Please enter at least one rule.",
        "warning"
      );
      return;
    }

    const firstEmptyIndex = trimmedRules.findIndex((r) => !r);
    if (firstEmptyIndex !== -1) {
      await Swal.fire(
        "Rule text required",
        `Rule #${firstEmptyIndex + 1} cannot be empty.`,
        "warning"
      );
      return;
    }

    if (termsEnabled && !termsHeading.trim()) {
      await Swal.fire(
        "Terms and condition heading required",
        "Terms and condition heading cannot be empty while the toggle is on.",
        "warning"
      );
      return;
    }

    if (termsEnabled && !termsText.trim()) {
      await Swal.fire(
        "Terms and condition required",
        "Terms and condition message cannot be empty while the toggle is on.",
        "warning"
      );
      return;
    }

    const payload = {
      data: {
        rules: trimmedRules,
        follow_text: title,
        step1_text: step1Text,
        step2_text: step2Text,
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_text_color: colors.button_text_color,
        leaderboard_color: colors.leaderboard_color,
        table_color1: colors.table_color1,
        table_color2: colors.table_color2,
        gallery_enabled: galleryEnabled,
        gallery_heading: galleryHeading,
        terms_enabled: termsEnabled,
        terms_heading: termsEnabled ? termsHeading.trim() : termsHeading,
        terms_text: termsEnabled ? termsText.trim() : termsText,
        tab_name: tabName,
        upload_instruction_text: uploadInstructionText,
        animated_avatar_text: animatedAvatarText,
        reveal_page_text: revealPageText,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      // Swal.fire("Success", "Settings saved successfully!", "success");
      setTimeout(() => navigate("/admin/themeUpdate"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

          <div className="back-button-holder">
              <button
                className="back-button"
                onClick={() => navigate("/admin/")}
              >
              <i className="fa-solid fa-arrow-left"></i> Back
              </button>
          </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Basic details
                </div>
                <div className="form-group-v1">
                  <label>Heading</label>
                  <textarea
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Heading"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={HEADING_MAX_LEN}
                  />
                  <div className="code">* Maximum {HEADING_MAX_LEN} characters.</div>
                </div>

                <div className="form-group-v1">
                  <label>Tab name</label>
                  <input
                    type="text"
                    value={tabName}
                    onChange={(e) => handleTabNameChange(e.target.value)}
                    placeholder="Tab name"
                    className="form-control"
                    maxLength={TAB_NAME_MAX_LEN}
                  />
                  <div className="code">{tabName.length}/{TAB_NAME_MAX_LEN}</div>
                </div>

                <div className="form-group-v1">
                  <label>Upload instruction message</label>
                  <textarea
                    value={uploadInstructionText}
                    onChange={(e) => handleUploadInstructionChange(e.target.value)}
                    placeholder="Upload instruction message"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={UPLOAD_INSTRUCTION_MAX_LEN}
                  />
                  <div className="code">{uploadInstructionText.length}/{UPLOAD_INSTRUCTION_MAX_LEN}</div>
                </div>

                <div className="form-group-v1">
                  <label>Animated avatar text</label>
                  <textarea
                    value={animatedAvatarText}
                    onChange={(e) => handleAnimatedAvatarChange(e.target.value)}
                    placeholder="Animated avatar text"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={ANIMATED_AVATAR_MAX_LEN}
                  />
                  <div className="code">{animatedAvatarText.length}/{ANIMATED_AVATAR_MAX_LEN}</div>
                </div>

                <div className="form-group-v1">
                  <label>Reveal page text</label>
                  <textarea
                    value={revealPageText}
                    onChange={(e) => handleRevealPageTextChange(e.target.value)}
                    placeholder="Reveal page text"
                    className="form-control rules-textarea"
                    rows={2}
                    maxLength={REVEAL_PAGE_TEXT_MAX_LEN}
                  />
                  <div className="code">{revealPageText.length}/{REVEAL_PAGE_TEXT_MAX_LEN}</div>
                </div>
              </div>
            </div>

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game rules
                </div>
                <h3 className="custom-rules-title">Custom rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <input
                      className="rule-input"
                      type="text"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveRule(i)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <div>
                    <button className="add-btn" onClick={handleAddRule}>
                      + Add New Rule
                    </button>
                  </div>
                  <div className="code">
                    * Keep 6 rules at the most. <br></br>* At least 1 rule is
                    required.<br></br>* Character limit of each rule is 200<br></br>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Color customization
                </div>
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_text_color}
                          onChange={handleColorChange("button_text_color")}
                        />
                        <label>Button text color</label>
                      </div>
                      <div className="color-code">{colors.button_text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-images"></i> &nbsp; Gallery
                </div>
                <div className="form-group-v1">
                  <label>Gallery heading</label>
                  <input
                    type="text"
                    value={galleryHeading}
                    onChange={(e) => handleGalleryHeadingChange(e.target.value)}
                    placeholder="Gallery"
                    className="form-control"
                    maxLength={GALLERY_HEADING_MAX_LEN}
                  />
                  <div className="code">{galleryHeading.length}/{GALLERY_HEADING_MAX_LEN}</div>
                </div>
                <div className="gallery-toggle-row">
                  <span>{galleryEnabled ? "Gallery is shown to players" : "Gallery is hidden from players"}</span>
                  <label className="theme-toggle">
                    <input
                      type="checkbox"
                      checked={galleryEnabled}
                      onChange={(e) => setGalleryEnabled(e.target.checked)}
                    />
                    <span className="theme-toggle-slider" />
                  </label>
                </div>
              </div>

              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-file-contract"></i> &nbsp; Terms and condition
                </div>
                {termsEnabled && (
                  <div className="form-group-v1">
                    <label>Terms and condition heading</label>
                    <input
                      type="text"
                      value={termsHeading}
                      onChange={(e) => handleTermsHeadingChange(e.target.value)}
                      placeholder="Terms and condition heading"
                      className="form-control"
                      maxLength={TERMS_HEADING_MAX_LEN}
                    />
                    <div className="code">{termsHeading.length}/{TERMS_HEADING_MAX_LEN}</div>
                    <label>Terms and condition message</label>
                    <textarea
                      value={termsText}
                      onChange={(e) => handleTermsTextChange(e.target.value)}
                      placeholder="Terms and condition message"
                      className="form-control rules-textarea"
                      rows={4}
                      maxLength={TERMS_MAX_LEN}
                    />
                    <div className="code">{termsText.length}/{TERMS_MAX_LEN}</div>
                  </div>
                )}
                <div className="gallery-toggle-row">
                  <span>{termsEnabled ? "Terms are shown to players" : "Terms are hidden from players"}</span>
                  <label className="theme-toggle">
                    <input
                      type="checkbox"
                      checked={termsEnabled}
                      onChange={(e) => setTermsEnabled(e.target.checked)}
                    />
                    <span className="theme-toggle-slider" />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & continue
            </button>

          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
